import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import mapProps from 'map-props';
import { values } from 'lodash/fp';
import cns from 'classnames';

import { withTracker, hitTypes } from 'helpers/tracking';
import { formatMoney } from 'helpers/i18n';
import * as sellerTypes from 'constants/sellerType';

import Link from 'components/Link';
import SellerBadge from 'components/SellerBadge';
import FavoriteSwitcher from 'components/FavoriteSwitcher';
import DislikeAdButton from 'components/DislikeAdButton';
import ImageContainer, { DESKTOP, MOBILE } from './ImageContainer';
import SellerInfo from './SellerInfo';
import SellerContactInfo from './SellerContactInfo';

import propsMapping from './propsMapping';
import cn from './styles.local.less';

class AdCard extends PureComponent {
  static propTypes = {
    id: PropTypes.string.isRequired,
    isDislike: PropTypes.bool.isRequired,
    isFavorite: PropTypes.bool.isRequired,
    className: PropTypes.string,
    desktopImages: PropTypes.array,
    exclusive: PropTypes.bool,
    formattedCo2Value: PropTypes.string.isRequired,
    formattedFuelConsumption: PropTypes.string.isRequired,
    formattedHorsePower: PropTypes.string.isRequired,
    formattedMileage: PropTypes.string,
    formattedRegistration: PropTypes.string,
    formattedMonthlyPrice: PropTypes.string,
    fuel: PropTypes.string.isRequired,
    gear: PropTypes.string.isRequired,
    has360Images: PropTypes.bool.isRequired,
    isGridView: PropTypes.bool,
    isRetail: PropTypes.bool,
    link: PropTypes.string.isRequired,
    location: PropTypes.object,
    mobileImages: PropTypes.array,
    monthlyPrice: PropTypes.number,
    nanoImage: PropTypes.string,
    price: PropTypes.number.isRequired,
    published: PropTypes.bool,
    sellerLogo: PropTypes.string,
    sellerName: PropTypes.string,
    sellerType: PropTypes.oneOf(values(sellerTypes)).isRequired,
    showMonthlyPrice: PropTypes.bool,
    subtitle: PropTypes.string,
    title: PropTypes.string.isRequired,
    tracker: PropTypes.object,
    trackAs: PropTypes.string.isRequired,
    vatRate: PropTypes.string,
    imageDisplayType: PropTypes.string,
    sellerContactInfoProps: PropTypes.object,
    withVehicleExtraInfo: PropTypes.bool,
    withConsumption: PropTypes.bool,
    withMerchantInfo: PropTypes.bool,
    withTransmission: PropTypes.bool,
    withLocation: PropTypes.bool,
    withSubtitle: PropTypes.bool,
    withSellerContactInfo: PropTypes.bool,
    withFavorite: PropTypes.bool,
    withDislike: PropTypes.bool,
    showFinanceCalculatorLink: PropTypes.bool.isRequired,
    onToggleAdDislikeClick: PropTypes.func,
    onToggleAdFavoriteClick: PropTypes.func,
  };

  static defaultProps = {
    withTransmission: true,
    withVehicleExtraInfo: true,
    withSellerContactInfo: false,
    withSubtitle: false,
    withFavorite: true,
    withDislike: true,
  };

  handleClick = () => {
    const { tracker, sellerType } = this.props;

    tracker.track(hitTypes.AD_CLICK, sellerType);
  };

  handleFinanceLinkClick = (e) => {
    const { tracker } = this.props;

    e.stopPropagation();

    tracker.track(hitTypes.AD_CARD_MONTHLY_RATE_CLICK);
  };

  handleFavoriteClick = (isFavorite) => {
    const { tracker, trackAs } = this.props;

    tracker.track(hitTypes.AD_CARD_FAVORITE_CLICK, {
      trackAs,
      label: `Favorites - Icon ${isFavorite ? 'On' : 'Off'}`,
    });
  };

  handleDislikeAdClick = (isDislike) => {
    const { id, tracker, trackAs, onToggleAdDislikeClick } = this.props;

    tracker.track(hitTypes.AD_CARD_DISLIKE_CLICK, {
      trackAs,
      label: `Dislikes - Icon ${isDislike ? 'On' : 'Off'}`,
    });

    onToggleAdDislikeClick(id, isDislike);
  };

  renderMeta() {
    const {
      formattedHorsePower,
      formattedMileage,
      formattedRegistration,
      withTransmission,
      imageDisplayType,
      fuel,
      gear,
    } = this.props;

    return (
      <ul
        className={cns(cn.list, {
          [cn.listWrap]: imageDisplayType === 'secondary',
        })}
      >
        {formattedRegistration && (
          <li data-qa-selector="registration">{formattedRegistration}</li>
        )}

        {formattedMileage && (
          <li data-qa-selector="mileage">{formattedMileage}</li>
        )}

        {withTransmission && (
          <li
            className={cns({
              [cn.transmission]: imageDisplayType !== 'secondary',
            })}
            data-qa-selector="transmission"
            title={gear}
          >
            {gear}
          </li>
        )}

        <li data-qa-selector="fuelType">{fuel}</li>
        <li data-qa-selector="horsePower">{formattedHorsePower} </li>
      </ul>
    );
  }

  render() {
    const {
      className,
      desktopImages,
      exclusive,
      id,
      formattedCo2Value,
      formattedFuelConsumption,
      formattedMonthlyPrice,
      isGridView,
      has360Images,
      isFavorite,
      isDislike,
      link,
      location,
      mobileImages,
      monthlyPrice,
      price,
      published,
      sellerLogo,
      sellerName,
      sellerType,
      showMonthlyPrice,
      subtitle,
      title,
      vatRate,
      withConsumption,
      withLocation,
      withFavorite,
      withDislike,
      withMerchantInfo,
      withVehicleExtraInfo,
      withSellerContactInfo,
      withSubtitle,
      showFinanceCalculatorLink,
      imageDisplayType,
      sellerContactInfoProps,
      onToggleAdFavoriteClick,
    } = this.props;

    return (
      <div
        className={cns(cn.root, className, 'withTracking', {
          [cn.gridView]: isGridView,
        })}
        onClick={this.handleClick}
        data-qa-selector="ad-item"
      >
        {withFavorite || withDislike ? (
          <div className={cn.favoriteBlock}>
            {withFavorite ? (
              <FavoriteSwitcher
                isFavorite={isFavorite}
                adId={id}
                onToggleAdFavoriteClick={onToggleAdFavoriteClick}
                onClick={this.handleFavoriteClick}
              />
            ) : null}

            {withDislike ? (
              <DislikeAdButton
                adId={id}
                onClick={this.handleDislikeAdClick}
                isDislike={isDislike}
              />
            ) : null}
          </div>
        ) : null}

        <ImageContainer
          isExclusive={exclusive}
          has360Images={has360Images}
          className={cn.imageContainer}
          src={desktopImages}
          type={DESKTOP}
          isGridView={isGridView}
          displayType={imageDisplayType}
          isAvailable={published}
          link={link}
        />

        <div className={cn.main}>
          <Link
            data-qa-selector="title"
            className={cns(cn.title, { [cn.titleInGrid]: isGridView })}
            to={{ pathname: link }}
            target="_blank"
          >
            {title}
            <div className={cn.clicker} />
          </Link>
          {withSubtitle ? (
            <div
              data-qa-selector="subtitle"
              className={cns(cn.subtitle, { hidden: isGridView })}
            >
              {subtitle}
            </div>
          ) : null}

          <div className={cn.content}>
            {!isGridView ? (
              <ImageContainer
                isExclusive={exclusive}
                has360Images={has360Images}
                src={mobileImages}
                type={MOBILE}
                isAvailable={published}
                link={link}
              />
            ) : null}

            {this.renderMeta()}
          </div>
          {withVehicleExtraInfo && withConsumption ? (
            <div className={cn.vehicleExtraInfo}>
              {formattedCo2Value ? (
                <span
                  data-qa-selector="co2Value"
                  className={cn.vehicleExtraInfoItem}
                >
                  {formattedCo2Value}
                </span>
              ) : null}

              {formattedFuelConsumption ? (
                <span
                  data-qa-selector="fuelConsumption"
                  className={cn.vehicleExtraInfoItem}
                >
                  {formattedFuelConsumption}
                </span>
              ) : null}
            </div>
          ) : null}

          <footer className={cn.footer}>
            <div className={cn.sellerPriceBlock}>
              <div className={cns(cn.seller, { hidden: isGridView })}>
                <SellerBadge sellerType={sellerType} />
                {withLocation && location ? (
                  <SellerInfo
                    location={location}
                    sellerLogo={sellerLogo}
                    sellerName={sellerName}
                    sellerType={sellerType}
                    withMerchantInfo={withMerchantInfo}
                  />
                ) : null}
              </div>
              <div className={cns({ 'text-right': !isGridView })}>
                <div
                  data-qa-selector-value={price}
                  data-qa-selector="price"
                  className={cn.priceValue}
                >
                  {formatMoney(price)}
                </div>

                <div className={cn.monthlyPriceDetails}>
                  {vatRate && <span className={cn.percent}>{vatRate}</span>}

                  {showFinanceCalculatorLink ? (
                    <Link
                      className={cn.monthlyPrice}
                      onClick={this.handleFinanceLinkClick}
                      to={`${link}?scrollToFinanceCalculator=true`}
                    >
                      <span
                        data-qa-selector-value={monthlyPrice}
                        data-qa-selector="monthly-price"
                      >
                        {formattedMonthlyPrice}
                      </span>
                    </Link>
                  ) : null}

                  {!showFinanceCalculatorLink &&
                  showMonthlyPrice &&
                  monthlyPrice ? (
                    <div className={cn.percent}>
                      {__('retail.monthlyPrice.label')}{' '}
                      <b
                        data-qa-selector-value={monthlyPrice}
                        data-qa-selector="monthly-price"
                      >
                        {formatMoney(monthlyPrice)}*
                      </b>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {withSellerContactInfo ? (
              <div className={cn.sellerContactBlock}>
                <SellerContactInfo {...sellerContactInfoProps} />
              </div>
            ) : null}
          </footer>
        </div>
      </div>
    );
  }
}

export default withTracker(mapProps(propsMapping)(AdCard));
